import { useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import "./MemoryGame.css";
import { host } from "../globalVariables";
import { Button } from "react-bootstrap";

const KARTE_MIN_BREITE = 80; // px
const KARTE_MAX_BREITE = 150; // px
const KARTE_SEITENVERHAELTNIS = 4 / 3; // Höhe = Breite * 4/3 (passt zum CSS aspect-ratio: 3/4)
const GRID_LUECKE = 10; // px – muss zum gap im CSS passen (siehe Punkt 5)
const STORAGE_KEY_PREFIX = "mg-mitarbeitermemo-state-";

/**
 * Erwartetes Datenformat pro Mitarbeiter:
 * {
 *   id: "max_mustermann",       // eindeutiger Key, z.B. vorname_nachname
 *   Vorname: "Max",
 *   Nachname: "Mustermann",
 *   Kuerzel: "MM",
 *   standort: "Berlin",
 *   abteilung: "Frontend Developer"
 * }
 *
 * Das Foto wird automatisch aus vorname_nachname.jpg gebaut und über
 * photoBaseUrl (Node/Express-Route, die den Fotoordner statisch ausliefert) geladen.
 */

// Fisher-Yates Shuffle
function shuffle(array) {
   const a = [...array];
   for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
   }
   return a;
}

function waehleZufaelligeMitarbeiter(liste, anzahl) {
   return [...liste].sort(() => Math.random() - 0.5).slice(0, anzahl);
}

function buildDeck(mitarbeiter) {
   const cards = mitarbeiter.flatMap((m) => [
      { uid: `${m.id}-foto`, employeeId: m.id, type: "foto", data: m },
      { uid: `${m.id}-info`, employeeId: m.id, type: "info", data: m },
   ]);
   return shuffle(cards);
}

function getStorageKey(user) {
   return `${STORAGE_KEY_PREFIX}${user?.username || "anon"}`;
}

function ladeGespeichertenZustand(user) {
   try {
      const raw = window.localStorage.getItem(getStorageKey(user));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
   } catch (e) {
      console.warn("Konnte gespeicherten Memo-Zustand nicht laden:", e);
      return {};
   }
}

export default function MemoryGame({
   mitarbeiter,
   anzahl = 9,
   updateUserData,
   setAnzahl = () => {},
   photoBaseUrl = "/api/mitarbeiterfotos",
   onWin,
   user,
}) {
   // Wird nur beim allerersten Rendern befüllt und danach nicht mehr verändert
   const gespeichertRef = useRef(null);
   if (gespeichertRef.current === null) {
      gespeichertRef.current = ladeGespeichertenZustand(user);
   }
   const gespeichert = gespeichertRef.current;

   const [hardcore, setHardcore] = useState(gespeichert.hardcore ?? false);
   const [timerAktiv, setTimerAktiv] = useState(gespeichert.timerAktiv ?? false);
   const [sekunden, setSekunden] = useState(() => {
      if (gespeichert.timerAktiv && gespeichert.startZeit) {
         // Zeit seit dem letzten Speichern korrekt weiterlaufen lassen
         return Math.floor((Date.now() - gespeichert.startZeit) / 1000);
      }
      return gespeichert.sekunden ?? 0;
   });
   const [deck, setDeck] = useState(() => {
      if (Array.isArray(gespeichert.deck) && gespeichert.deck.length) {
         return gespeichert.deck;
      }
      return buildDeck(waehleZufaelligeMitarbeiter(mitarbeiter, anzahl));
   });
   const [flipped, setFlipped] = useState(gespeichert.flipped ?? []); // uids der aktuell aufgedeckten Karten (max 2)
   const [matched, setMatched] = useState(() => new Set(gespeichert.matched ?? [])); // employeeIds, die gelöst sind
   const [versuche, setVersuche] = useState(gespeichert.versuche ?? 0);
   const [locked, setLocked] = useState(false); // nach Reload nie gesperrt starten
   const startZeitRef = useRef(gespeichert.startZeit ?? null);
   const [endTime, setEndTime] = useState();
   const gridRef = useRef(null);
   const outerRef = useRef(null); // der scrollbare Wrapper mit fester Höhe
   const [kartenBreite, setKartenBreite] = useState(KARTE_MAX_BREITE);
   const [spalten, setSpalten] = useState(1);
   const gewonnen = matched.size === anzahl;

   // Falls der gespeicherte Zustand zu einer anderen Kartenanzahl gehört
   // als der Elternkomponente bekannt ist, den Elternstate einmalig angleichen.
   useEffect(() => {
      if (gespeichert.anzahl && gespeichert.anzahl !== anzahl) {
         setAnzahl(gespeichert.anzahl);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (!timerAktiv || gewonnen) return;

      const intervalId = setInterval(() => {
         const vergangeneMs = Date.now() - startZeitRef.current;
         setSekunden(vergangeneMs / 1000);
      }, 100); // öfter prüfen als anzeigen -> Anzeige wirkt trotzdem exakt zur Sekunde

      return () => clearInterval(intervalId);
   }, [timerAktiv, gewonnen]);

   // Aktuellen Spielstand bei jeder relevanten Änderung persistieren
   useEffect(() => {
      try {
         const zustand = {
            hardcore,
            deck,
            flipped,
            matched: Array.from(matched),
            versuche,
            sekunden,
            timerAktiv,
            startZeit: startZeitRef.current,
            anzahl,
         };
         window.localStorage.setItem(getStorageKey(user), JSON.stringify(zustand));
      } catch (e) {
         console.warn("Konnte Memo-Zustand nicht speichern:", e);
      }
   }, [hardcore, deck, flipped, matched, versuche, sekunden, timerAktiv, anzahl, user]);

   useLayoutEffect(() => {
      function berechneLayout() {
         const el = gridRef.current;
         const outer = outerRef.current;
         if (!el || !outer) return;

         // WICHTIG: nicht window.innerHeight - getBoundingClientRect().top verwenden!
         // Das hängt von der Scroll-Position der ganzen Seite ab. Da oberhalb des
         // Memory-Spiels viel Inhalt steht (Spielregeln-Alert + Rangliste-Tabelle),
         // steht das Grid beim allerersten Laden (Scroll-Position 0) oft noch
         // unterhalb des sichtbaren Bereichs -> "top" > innerHeight -> negative
         // "verfügbare Höhe" -> Layout kollabiert auf 1 Spalte. Der outer-Container
         // hat dagegen eine FESTE Höhe (calc(100vh - 80px)), egal wie die Seite
         // gescrollt ist oder wo das Grid auf der Seite steht.
         const verfuegbareBreite = outer.clientWidth;
         const verfuegbareHoehe = outer.clientHeight - 16;

         if (verfuegbareHoehe <= 0 || verfuegbareBreite <= 0) return;

         const anzahlKarten = deck.length;
         let besteBreite = 0;
         let besteSpalten = 1;

         for (let spalten = 1; spalten <= anzahlKarten; spalten++) {
            const zeilen = Math.ceil(anzahlKarten / spalten);
            const breiteAusBreite = (verfuegbareBreite - (spalten - 1) * GRID_LUECKE) / spalten;
            const breiteAusHoehe = (verfuegbareHoehe - (zeilen - 1) * GRID_LUECKE) / zeilen / KARTE_SEITENVERHAELTNIS;
            const moeglicheBreite = Math.min(breiteAusBreite, breiteAusHoehe);
            if (moeglicheBreite > besteBreite) {
               besteBreite = moeglicheBreite;
               besteSpalten = spalten;
            }
         }

         const geklemmteBreite = Math.min(KARTE_MAX_BREITE, Math.max(KARTE_MIN_BREITE, besteBreite));
         setKartenBreite(geklemmteBreite);
         setSpalten(besteSpalten);
      }

      berechneLayout();

      // Nur noch den outer-Container beobachten - das ist der einzige Wert,
      // von dem die Berechnung jetzt abhängt (kein Header, kein window-resize
      // auf Basis der Scroll-Position mehr nötig).
      const outer = outerRef.current;
      const ro = new ResizeObserver(() => berechneLayout());
      if (outer) ro.observe(outer);

      // Fallback: falls die erste Messung trotzdem zu früh war
      // (z.B. Fonts laden asynchron nach und ändern noch mal minimal die
      // Größe des Containers, ohne dass ResizeObserver das mitbekommt)
      const t1 = setTimeout(berechneLayout, 150);
      const t2 = setTimeout(berechneLayout, 500);

      return () => {
         ro.disconnect();
         clearTimeout(t1);
         clearTimeout(t2);
      };
   }, [deck.length]);

   useEffect(() => {
      if (timerAktiv && gewonnen && (anzahl === 16 || anzahl === 4)) {
         const vergangeneS = (Date.now() - startZeitRef.current) / 1000;
         setSekunden(vergangeneS);
         updateUserData({ zeit: vergangeneS, versuche: versuche, t: new Date(), anzahl: anzahl });
      }

      if (gewonnen && onWin) onWin(versuche);
      if (gewonnen) setTimerAktiv(false);
   }, [gewonnen]); // eslint-disable-line react-hooks/exhaustive-deps

   const neuStarten = (anzahl) => {
      setDeck(buildDeck(waehleZufaelligeMitarbeiter(mitarbeiter, anzahl)));

      setAnzahl(anzahl);
      setFlipped([]);
      setMatched(new Set());
      setVersuche(0);
      setLocked(false);
      setSekunden(0);
      setTimerAktiv(false);
      startZeitRef.current = null;
   };

   function formatiereZeit(gesamtSekunden) {
      const minuten = Math.floor(gesamtSekunden / 60)
         .toString()
         .padStart(2, "0");
      // const rest = (gesamtSekunden % 60).toString().padStart(2, "0")
      const rest = (gesamtSekunden % 60).toFixed(3).padStart(6, "0");
      return `${minuten}:${rest}`;
   }

   const handleClick = (card) => {
      if (locked) return;
      if (flipped.includes(card.uid)) return;
      if (matched.has(card.employeeId) && flipped.length < 2) return;

      if (!timerAktiv) {
         startZeitRef.current = Date.now(); // <- geändert: echter Zeitstempel statt nur Flag
         setTimerAktiv(true);
      }

      const naechsteFlipped = [...flipped, card.uid];
      setFlipped(naechsteFlipped);

      if (naechsteFlipped.length === 2) {
         setLocked(true);
         setVersuche((v) => v + 1);

         const [ersteUid, zweiteUid] = naechsteFlipped;
         const erste = deck.find((c) => c.uid === ersteUid);
         const zweite = deck.find((c) => c.uid === zweiteUid);

         const istPaar = erste.employeeId === zweite.employeeId && erste.type !== zweite.type;

         window.setTimeout(
            () => {
               if (istPaar) {
                  setMatched((prev) => new Set(prev).add(erste.employeeId));
               }
               setFlipped([]);
               setLocked(false);
            },
            istPaar ? 500 : 900,
         );
      }
   };

   return (
      <div className="mg-wrapper">
         <div className="mg-header">
            <h2 className="mg-title">Mitarbeiter-Memo</h2>
            <div className="mg-stats">
               <span>Schwierigkeitsgrad:</span>
               {import.meta.env.DEV && (
                  <Button
                     variant="outline-dark"
                     disabled={timerAktiv}
                     onClick={() => {
                        setHardcore(false);
                        neuStarten(4);
                     }}
                  >
                     Test
                  </Button>
               )}
               <Button
                  variant="outline-dark"
                  disabled={timerAktiv}
                  onClick={() => {
                     setHardcore(false);
                     neuStarten(9);
                  }}
               >
                  Trainingslevel
               </Button>
               {
                  <Button
                     variant="outline-dark"
                     disabled={
                        /* !user.username || user.username !== "JonathanMischke" && */
                        (new Date() < new Date("2026-09-18T15:40:00Z") &&
                           mitarbeiter.filter((item) => item.Name === user.username)[0]?.ranking?.mitarbeitermemo) ||
                        new Date() > new Date("2026-09-20T18:00:00Z") /* ||
                         */
                     }
                     onClick={() => {
                        setHardcore(false);
                        neuStarten(16);
                     }}
                  >
                     Qualifikation
                  </Button>
               }
               <Button
                  variant="outline-dark"
                  disabled
                  onClick={() => {
                     neuStarten(mitarbeiter.length - 1);
                     setHardcore(false);
                  }}
               >
                  Finale
               </Button>
               <Button
                  variant="outline-dark"
                  disabled={timerAktiv}
                  onClick={() => {
                     neuStarten(mitarbeiter.length - 1);
                     setHardcore(true);
                  }}
               >
                  Fast unmöglich
               </Button>
               <span className="mg-timer">
                  Zeit: <strong>{formatiereZeit(sekunden)}</strong>
               </span>
               <span className="mg-versuche">
                  Versuche: <strong>{versuche}</strong>
               </span>
               <span className="mg-fortschritt">
                  {matched.size} / {anzahl} Paare
               </span>

               <Button
                  disabled={
                     anzahl === 16 /* &&
                     mitarbeiter.filter((item) => item.Name === user.username)[0]?.ranking?.mitarbeitermemo */
                  }
                  variant="dark"
                  onClick={() => {
                     neuStarten(anzahl);
                  }}
               >
                  Neu starten
               </Button>
            </div>
         </div>

         {gewonnen && (
            <div className="mg-win-banner">
               Geschafft! Du hast alle Paare in {versuche} Versuchen und {sekunden} Sekunden gefunden.
            </div>
         )}

         <div
            ref={outerRef}
            style={{
               overflow: "auto",
               width: "100%",
               height: "calc(100vh - 80px)",
               padding: "0px",
            }}
         >
            <div
               ref={gridRef}
               className="mg-grid"
               style={{
                  gridTemplateColumns: `repeat(${spalten}, ${kartenBreite}px)`,
                  // justifyContent: "center",
               }}
            >
               {deck.map((card) => {
                  const istAufgedeckt = flipped.includes(card.uid) || matched.has(card.employeeId);
                  return (
                     <Karte
                        hardcore={hardcore}
                        key={card.uid}
                        card={card}
                        aufgedeckt={istAufgedeckt}
                        gelöst={matched.has(card.employeeId)}
                        photoBaseUrl={photoBaseUrl}
                        onClick={() => handleClick(card)}
                     />
                  );
               })}
            </div>
         </div>
      </div>
   );
}

function Karte({ card, aufgedeckt, gelöst, photoBaseUrl, onClick, hardcore }) {
   return (
      <button
         className={`mg-card ${aufgedeckt ? "is-flipped" : ""} ${gelöst ? "is-solved" : ""}`}
         onClick={onClick}
         disabled={gelöst}
         aria-label={aufgedeckt ? kartenBeschriftung(card) : "Karte verdeckt"}
      >
         <div className="mg-card-inner">
            <div className="mg-card-face mg-card-back">
               <img
                  src={`${photoBaseUrl}Mitarbeiter/backside.jpg`}
                  alt={``}
                  style={{ objectPosition: "center" }}
                  className="mg-foto"
               />
            </div>
            <div
               className="mg-card-face mg-card-front"
               style={{
                  objectPosition: "center",
               }}
            >
               {card.type === "foto" ? (
                  <FotoSeite data={card.data} photoBaseUrl={photoBaseUrl} hardcore={hardcore} />
               ) : (
                  <InfoSeite data={card.data} photoBaseUrl={photoBaseUrl} />
               )}
            </div>
         </div>
      </button>
   );
}

function FotoSeite({ data, photoBaseUrl, hardcore }) {
   const [fehler, setFehler] = useState(false);
   const dateiname = `${encodeURIComponent(data.Vorname.replace(" ", ""))}_${encodeURIComponent(data.Nachname.replace(" ", ""))}.jpg`;
   const src = `${photoBaseUrl}Mitarbeiter/${dateiname}`;

   return (
      <div className="mg-foto-seite">
         <div
            style={{
               position: "absolute",
               color: "#96c11f",
               background: "#0009",
               padding: "2px 6px",
               backdropFilter: "blur(4px)",
               fontSize: 14,
               borderRadius: 6,
               boxShadow: "0 0 10px #fff",
               margin: "0.6rem ",
               right: "0%",
               bottom: "0%",
               fontWeight: 900,
            }}
         >
            {data.Kuerzel}
         </div>
         {!fehler ? (
            <img
               src={src}
               alt={`${data.Vorname} ${data.Nachname}`}
               className="mg-foto"
               style={{
                  ...(hardcore ? { filter: "blur(1px)" } : {}),
               }}
               onError={(err) => {
                  setFehler(true);
               }}
            />
         ) : (
            <div className="mg-foto-fallback" style={{ fontSize: 6, overflow: "show" }}>
               {src}
            </div>
         )}
         {hardcore && (
            <div
               style={{
                  position: "absolute",
                  inset: 0,
                  background: "radial-gradient(ellipse 100% 30% at 50% 25%, transparent 0%,#000e 33%, #000e 100%)",
                  pointerEvents: "none",
               }}
            />
         )}
      </div>
   );
}

function InfoSeite({ data, photoBaseUrl }) {
   return (
      <div
         className="mg-info-seite"
         style={{
            backgroundImage: `linear-gradient(#96c11f99, #96c11faa), url(${photoBaseUrl}Standorte/${encodeURIComponent(data.standort)}.jpg)`,
         }}
      >
         <div className="mg-info-Kuerzel">{data.Kuerzel}</div>
         <div className="mg-info-name">
            {data.Vorname} {data.Nachname}
         </div>
         <div className="mg-info-position">{data.abteilung}</div>
         <div className="mg-info-standort" style={{ wordBreak: "break-all" }}>
            {data.description}
         </div>

         <div className="mg-info-standort">📍 {data.standort}</div>
      </div>
   );
}
function kartenBeschriftung(card) {
   return card.type === "foto"
      ? `Foto von ${card.data.Vorname} ${card.data.Nachname}`
      : `Info: ${card.data.Vorname} ${card.data.Nachname}, ${card.data.standort}`;
}
